import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { MatDrawer } from '@angular/material/sidenav';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { finalize, takeUntil, tap } from 'rxjs/operators';

import { EFeatureFlag } from '@app/feature-toggle-list';
import { ProductTagService } from 'app/core/product-tags/services/product-tag.service';
import { ITagOld } from 'v2/modules/shared/interfaces/tag.interface';

// Configs

// Components

// Interfaces
import { IOrderSalesPicklistPreview } from '../../../order/interfaces/order-picklist-preview';
import { ISalesOrder } from '../../../order/interfaces/order.interface';
import { SnackBarConfig } from '../../../shared/configs/snackbar.config';
import {
  IAgGridSideMenuItemConfig,
  IAgGridSideMenuItem,
} from '../../../shared/interfaces/ag-grid';
import { LocalStorageKeys } from '../../../shared/interfaces/localstorage.interface';
import { IWarehouseGroup } from '../../../shared/interfaces/location-warehouse.interface';
import { IChannel } from '../../../shared/interfaces/mtchannel.interface';
import { ESalesOrderPermission } from '../../../shared/interfaces/permissions.interface';

// Pipes
import { MapEnumTextPipe } from '../../../shared/pipes/map-enum-text.pipe';

// Services
import { ChannelApiService } from '../../../shared/services/api/channel/channel-api.service';
import { LocationWarehouseGroupApiService } from '../../../shared/services/api/location/location-warehouse-group-api.service';
import { PicklistApiService } from '../../../shared/services/api/picklist-api.service';
import { SystemSettingApiService } from '../../../shared/services/api/system-setting-api.service';
import { FeatureFlagUtilService } from '../../../shared/services/util/feature-flag-util.service';
import { PermissionUtilService } from '../../../shared/services/util/permission-util.service';

// Utils
import { trackById } from '../../../shared/util/track-by.util';
import { SalesOrderAgTableConfig } from '../../configs/sales-order-ag-table.config';
import {
  ESalesOrderStatusHuman,
  ESalesOrderStatusValue,
  ISalesOrderStatus,
} from '../../interfaces/sales-order.interface';
import { PreviewPicklistUtilsService } from '../../services/preview-picklist-utils.service';

import { AgGridCustomFilterFormComponent } from './../../../shared/components/ag-grid-custom-filter-form/ag-grid-custom-filter-form.component';
import { DialogConfig } from './../../../shared/configs/dialog.config';
import {
  EAgGridFilter,
  EAgGridFilterActions,
  IAgGridFilter,
} from './../../../shared/interfaces/ag-grid-filter.interface';
import { AgGridFilterApiService } from './../../../shared/services/api/ag-grid-filter-api.service';
import { OrderApiService } from './../../../shared/services/api/order/order-api.service';

interface ISalesOrderData extends ISalesOrder {
  canUpdateOrderStatus :boolean;
}

@Component({
  selector: 'lum-sales-order',
  templateUrl: './sales-order.component.html',
  styleUrls: ['./sales-order.component.scss'],
})
export class SalesOrderComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild(MatDrawer)
  drawer! :MatDrawer;

  @Input()
  latestAdjustmentEntry :string;

  @Output()
  createAdjustmentEntryEvent = new EventEmitter<number>();

  @Input()
  latestTrackingNumber :string;

  @Output()
  createTrackingNumberEvent = new EventEmitter<{
    salesOrderId :number;
    trackingNumbers :string[];
  }>();

  @Input()
  latestOrderStatusUpdate :string;

  @Output()
  setToPendingEvent = new EventEmitter<number>();

  @Input()
  latestHoldOrderUpdate :string;

  @Output()
  holdOrderEvent = new EventEmitter<{
    id :number;
    orderNumber :string;
    source :string;
    shipstationOrderId ?:any;
  }>();

  @Input()
  latestPrintShippingLabelUpdate :string;

  @Input()
  latestSalesOrderUpdate :string;

  @Input()
  ftApplyPresets :boolean = false;

  @Input()
  ftCustomFilters :boolean = false;

  @Output()
  isOnSyncJobs :EventEmitter<boolean> = new EventEmitter();

  @Output()
  printShippingLabelEvent = new EventEmitter<any>();

  @Output()
  onClickOrderNumberEvent = new EventEmitter<number>();

  @Input()
  authUserId :number;

  salesOrderStatuses :ISalesOrderStatus[] = [];
  salesOrders :ISalesOrder[] = [];
  pickingItems :IOrderSalesPicklistPreview;
  loadingStatuses :boolean;
  loadingPicklistItems :boolean;
  inventoryPickAndPackEnabled :boolean;
  manualCloseDrawer :boolean;

  channels$ :Observable<IChannel[]>;
  warehouseGroups :IWarehouseGroup[] = [];
  pickingItemsLatestEntry :string;
  appIntegrationAccountId :number | null = null;

  orderStatus :string | null;
  customFilters$ = new BehaviorSubject<IAgGridFilter[]>([]);
  loadingCustomFilters$ = new BehaviorSubject<boolean>(false);
  currentCustomFilter :IAgGridFilter | null = null;
  activeFilter :string | null = ESalesOrderStatusValue.PENDING;
  sideMenuItems :IAgGridSideMenuItem[] = [];
  sideMenuItemConfig :IAgGridSideMenuItemConfig =
    SalesOrderAgTableConfig.sideMenuConfig;

  channelsHasPushOrderMapping :boolean = false;
  tags :ITagOld[] = [];

  trackById = trackById;
  private onDestroy$ = new Subject();

  constructor(
    private cd :ChangeDetectorRef,
    private orderApiService :OrderApiService,
    private channelApiService :ChannelApiService,
    private picklistApiService :PicklistApiService,
    private previewPicklistUtilsService :PreviewPicklistUtilsService,
    private snackBar :MatSnackBar,
    private systemSettingsService :SystemSettingApiService,
    private warehouseGroupService :LocationWarehouseGroupApiService,
    private featureFlagUtilService :FeatureFlagUtilService,
    private permissionUtilService :PermissionUtilService,
    private agGridFilterApiService :AgGridFilterApiService,
    private dialog :MatDialog,
    private router :Router,
    private activatedRoute :ActivatedRoute,
    private mapEnumTextPipe :MapEnumTextPipe,
    private productTagService :ProductTagService,
  ) {}

  ngOnInit() :void {
    this.initActiveRoute();
    this.getSalesOrderStatuses();
    this.getInventoryPickAndPackSettings();
    this.getChannel();
    this.getWarehouseGroup();
    this.getCustomFilters();
    this.#getTags();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  ngOnChanges(changes :SimpleChanges) :void {
    if (changes['latestAdjustmentEntry']?.currentValue) {
      this.pickingItemsLatestEntry = new Date().toISOString();
      this.getPickingItems();
    }

    if (changes['latestOrderStatusUpdate']?.currentValue) {
      this.getSalesOrderStatuses();
    }

    if (changes['latestHoldOrderUpdate']?.currentValue) {
      this.getSalesOrderStatuses();
    }
  }

  onShowPicklistItemsDrawer(salesOrders :ISalesOrder[]) :void {
    this.salesOrders = salesOrders;
    this.getPickingItems();
    this.drawer.open();
  }

  onKeepDrawerOpen() :void {
    if (!this.manualCloseDrawer) {
      this.drawer.open();
    } else {
      this.manualCloseDrawer = false;
    }
  }

  onClosePicklistPreviewEvent(event :boolean = false) :void {
    this.manualCloseDrawer = true;
    this.drawer.close();
  }

  onInitQuickFulfillmentEvent(event :string) :void {
    this.previewPicklistUtilsService.setAction(event);
  }

  onCreateAdjustmentEntry(picklistId :number) :void {
    this.createAdjustmentEntryEvent.emit(picklistId);
  }

  onReturnSelectedRows(event :ISalesOrder[]) :void {
    this.salesOrders = event;
  }

  onSyncJobs(isOnSyncJob :boolean) :void {
    this.isOnSyncJobs.emit(isOnSyncJob);
  }

  onGetAppIntegrationAccountId(event :number | null) :void {
    this.appIntegrationAccountId = event;
  }

  onClickOrderNumber(event :number) :void {
    this.onClickOrderNumberEvent.emit(event);
  }

  onSetStatus(status :string | null) :void {
    this.#setStatus(status);
    this.updateUrlQueryParams(status || '');
  }

  onSetFilter(data :{ action :string; filter :IAgGridFilter }) :void {
    const { action, filter } = data;
    switch (action) {
      case EAgGridFilterActions.SET:
        this.orderStatus = null;
        this.currentCustomFilter = filter.filters;

        this.updateUrlQueryParams(filter.name);
        break;
      case EAgGridFilterActions.EDIT:
        this.onEditFilter(filter);
        break;
      case EAgGridFilterActions.DELETE:
        this.onDeleteFilter(filter);
        break;
      default:
        break;
    }
  }

  onSaveFilter() :void {
    this.dialog
      .open(AgGridCustomFilterFormComponent, {
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => {
        if (value) {
          this.agGridFilterApiService
            .create({
              name: value.name,
              filters: JSON.parse(
                localStorage.getItem(LocalStorageKeys.SALES_ORDER_AG_STATE) || '{}',
              ),
              module: EAgGridFilter.SALES_ORDER,
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (response) => {
                this.customFilters$.next([...this.customFilters$.value, response]);

                this.snackBar.open('Filter successfully created.', 'Success', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });

                this.cd.detectChanges();
              },
              error: (response) => {
                this.snackBar.open(
                  response?.error || 'Failed to create filter',
                  'Fail',
                  {
                    duration: SnackBarConfig.DEFAULT.duration,
                  },
                );
              },
            });
        }
      });
  }

  onAgGridStateChanged() :void {
    this.currentCustomFilter = null;
  }

  onEditFilter(data :IAgGridFilter) :void {
    this.dialog
      .open(AgGridCustomFilterFormComponent, {
        width: DialogConfig.sizes.small,
        data: data,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => {
        if (value) {
          this.agGridFilterApiService
            .update(data.id, {
              ...data,
              name: value.name,
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (response) => {
                this.customFilters$.next([
                  ...this.customFilters.filter((filter) => filter.id !== data.id),
                  response,
                ]);

                this.snackBar.open('Filter successfully updated.', 'Success', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });

                this.cd.detectChanges();
              },
              error: (response) => {
                this.snackBar.open(
                  response?.error || 'Failed to update filter',
                  'Fail',
                  {
                    duration: SnackBarConfig.DEFAULT.duration,
                  },
                );
              },
            });
        }
      });
  }

  onDeleteFilter(data :IAgGridFilter) :void {
    this.agGridFilterApiService
      .delete(data.id)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.customFilters$.next([
            ...this.customFilters.filter((filter) => filter.id !== data.id),
          ]);

          this.snackBar.open(response?.success, 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });

          this.cd.detectChanges();
        },
        error: (response) => {
          this.snackBar.open(response?.error || 'Failed to delete filter', 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private initActiveRoute() :void {
    this.activatedRoute.queryParams
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((params) => {
        this.activeFilter = params['filter'];
        // this.activeFilter = params['filter'] || ESalesOrderStatusValue.PENDING;
        if (!this.activeFilter) {
          const pageState = localStorage.getItem(
            LocalStorageKeys.SALES_ORDER_AG_STATE,
          );
          const state = JSON.parse(pageState) || null;
          this.activeFilter =
            state?.activeCustomFilters?.orderStatuses ||
            ESalesOrderStatusValue.PENDING;
        }

        const filterIsStatus = Object.values(ESalesOrderStatusValue).includes(
          this.activeFilter as ESalesOrderStatusValue,
        );

        if (filterIsStatus) {
          this.#setStatus(this.activeFilter);
        }
      });
  }

  private updateUrlQueryParams(name :string) :void {
    // Update URL with the new filter
    this.activatedRoute.queryParams.subscribe((params) => {
      const newParams = { ...params, filter: name };
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: newParams,
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    });
  }

  private getCustomFilters() :void {
    this.loadingCustomFilters$.next(true);
    this.agGridFilterApiService
      .get({
        module: EAgGridFilter.SALES_ORDER,
      })
      .pipe(
        finalize(() => this.loadingCustomFilters$.next(false)),
        takeUntil(this.onDestroy$),
      )
      .subscribe({
        next: (response) => {
          const customFilters = response.data;
          if (this.activeFilter) {
            const filter = customFilters.find(
              (filter) => filter.name === this.activeFilter,
            );
            if (filter) {
              this.currentCustomFilter = filter.filters;
            }
          }
          this.customFilters$.next(customFilters);
        },
        error: (response) => {
          this.snackBar.open(
            response?.error || 'Failed to load custom filters',
            'Fail',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
      });
  }

  private getSalesOrderStatuses() :void {
    this.loadingStatuses = true;
    this.orderApiService
      .getOrderStatuses()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((salesOrderStatuses :ISalesOrderStatus[]) => {
        this.salesOrderStatuses = salesOrderStatuses;
        this.loadingStatuses = false;
        this.setMenuItems(salesOrderStatuses);
        this.cd.detectChanges();
      });
  }

  private setMenuItems(salesOrderStatuses :ISalesOrderStatus[]) :void {
    const _STATUSES_IN_ORDER = [
      ESalesOrderStatusValue.PENDING,
      ESalesOrderStatusValue.ON_HOLD,
      ESalesOrderStatusValue.AWAITING_SHIPMENT,
      ESalesOrderStatusValue.PARTIALLY_SHIPPED,
      ESalesOrderStatusValue.SHIPPED,
      ESalesOrderStatusValue.CANCELLED,
    ];
    const _ALL = {
      name: ESalesOrderStatusValue.ALL,
      label: 'All Orders',
      data: {
        orderStatus: null,
        orderStatusCount: null,
      },
      disabled: false,
      hideCount: true,
    };
    const items = [_ALL];

    for (const statusValue of _STATUSES_IN_ORDER) {
      const orderStatus = salesOrderStatuses?.find((status :any) => {
        return status.orderStatus === statusValue;
      });

      const status = {
        orderStatus: statusValue,
        orderStatusCount: orderStatus ? orderStatus.orderStatusCount : 0,
      };

      items.push({
        name: status.orderStatus,
        label: this.mapEnumTextPipe.transform(
          status.orderStatus,
          ESalesOrderStatusHuman,
        ),
        data: status,
        disabled:
          status.orderStatusCount <= 0 && orderStatus?.orderStatusCount !== null,
        hideCount: status.orderStatusCount === null,
      });
    }

    this.sideMenuItems = items;
  }

  private getChannel() :void {
    this.channels$ = this.channelApiService.get({ all: '1' }).pipe(
      tap((channels :IChannel[]) => {
        this.channelsHasPushOrderMapping = channels.some(
          (channel :IChannel) => channel.pushOrderIntegrationMapping?.id,
        );
      }),
    );
  }

  private getWarehouseGroup() :void {
    this.warehouseGroupService.get({});
    this.warehouseGroupService.warehouseGroups$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (warehouseGroups :IWarehouseGroup[]) => {
          this.warehouseGroups = warehouseGroups;
          this.cd.detectChanges();
        },
        error: (response) => {
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private getInventoryPickAndPackSettings() :void {
    this.systemSettingsService
      .getSettingsByKey('inventory_pick_and_pack_items')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((setValue :boolean) => {
        this.inventoryPickAndPackEnabled = setValue;
      });
  }

  private getPickingItems() :void {
    const orderIds :number[] = this.salesOrders
      .filter((selectedRow :ISalesOrder | null) => !!selectedRow)
      .map((selectedRow :ISalesOrder) => selectedRow.id);

    this.loadingPicklistItems = true;
    this.picklistApiService
      .getPickingItemsBySalesOrderIds({
        salesOrderIds: orderIds.join(','),
        includeWithPicklist: 'true',
      })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.loadingPicklistItems = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: (pickingItems :IOrderSalesPicklistPreview) => {
          this.pickingItems = pickingItems;
        },
        error: (response) => {
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  #getTags() :void {
    this.productTagService
      .getV2({}, true)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (tags) => {
          this.tags = tags;
          this.cd.detectChanges();
        },
      });
  }

  #setStatus(status :string | null) {
    this.currentCustomFilter = null;
    this.orderStatus = status;
    this.router.navigate([], {
      queryParams: { filter: status },
      queryParamsHandling: 'merge',
    });
  }

  get formattedSalesOrders() :ISalesOrderData[] {
    return this.salesOrders.map((salesOrder :ISalesOrder) => {
      return {
        ...salesOrder,
        ...{
          canUpdateOrderStatus:
            salesOrder.picklist &&
            salesOrder.orderStatus !== ESalesOrderStatusHuman.SHIPPED,
        },
      };
    });
  }

  get canUpdateCheckedOrderStatus() :boolean {
    return this.formattedSalesOrders.every((order) => order.canUpdateOrderStatus);
  }

  get picklistPreviewConfig() :{
    canUpdateCheckedOrderStatus :boolean;
    checkedCount :number;
  } {
    return {
      canUpdateCheckedOrderStatus: this.canUpdateCheckedOrderStatus,
      checkedCount: this.salesOrders.length,
    };
  }

  get salesOrderStorage() :any {
    return JSON.parse(
      localStorage.getItem(LocalStorageKeys.SALES_ORDER_FILTERS_SORTINGS) || '{}',
    );
  }

  get ftPickAndPackEnabled() :boolean {
    return this.featureFlagUtilService.isFeatureEnabled(EFeatureFlag.PICK_AND_PACK);
  }

  get ftEdiIntegrationEnabled() :boolean {
    return this.featureFlagUtilService.isFeatureEnabled(
      EFeatureFlag.EDI_INTEGRATION,
    );
  }

  get ftRemoveWoocommerceFromChannelType() :boolean {
    return this.featureFlagUtilService.isFeatureEnabled(
      EFeatureFlag.REMOVE_WOOCOMMERCE_FROM_CHANNEL_TYPES,
    );
  }

  get ftApprovalModuleEnabled() :boolean {
    return this.featureFlagUtilService.isFeatureEnabled(
      EFeatureFlag.APPROVAL_MODULE,
    );
  }

  get canMarkAsShipped() :boolean {
    return this.permissionUtilService.can(
      ESalesOrderPermission.SALES_ORDER_MARK_SHIPPED,
    );
  }

  get canDeleteSalesOrder() :boolean {
    return this.permissionUtilService.can(ESalesOrderPermission.SALES_ORDER_DELETE);
  }

  get childComponentConfigurations() :{
    picklistPreviewConfig :{
      canUpdateCheckedOrderStatus :boolean;
      checkedCount :number;
    };
    loadingPicklistItems :boolean;
  } {
    return {
      picklistPreviewConfig: this.picklistPreviewConfig,
      loadingPicklistItems: this.loadingPicklistItems,
    };
  }

  get customFilters() :IAgGridFilter[] {
    return this.customFilters$.value;
  }

  get loadingCustomFilters() :boolean {
    return this.loadingCustomFilters$.value;
  }
}
